// Unified Web Audio playback for STANDALONE TAKE/CLIP playback.
//
// Replaces ad-hoc `new Audio(url)` patterns scattered across TTS take chips,
// VersionsBar version-pill streaming, and (where they fetch from URLs) any
// future spot that needs a single, sample-accurate "play this buffer now"
// helper. Built on top of the shared AudioContext in `audioContext.ts` so
// playback inherits the existing Safari MediaStream tap + invalidate-aware
// recovery, and can optionally route through the mixer's voice-gain bus so
// the same fader / mute / ducking that govern the timeline preview also
// govern these one-off plays.
//
// Why this exists (the win over HTMLMediaElement):
//   1. Safari's HTMLAudioElement has a ~200-500ms startup latency for short
//      blob URLs (incomplete prefetch + buffer-and-go semantics). An
//      AudioBufferSourceNode is sample-accurate — `start(when)` queues at
//      the exact context-time we ask for.
//   2. Decoded buffers can be cached. A second play of the same take is
//      instant (no re-decode, no re-fetch). HTMLAudio can't share decoded
//      state between elements.
//   3. Single API for the whole app. Take chips, version pills, future
//      previewers all call through the same cancel/seek/poll surface.
//
// What this is NOT for:
//   • The master Timeline preview (`useTimelinePreview`) and the STS master
//     preview already implement multi-source scheduling with bed routing,
//     duck transitions, and rAF playhead sync. They have their own logic and
//     are not migrated through this helper.
//   • The video element's own audio. The <video> must use its native pipeline
//     for frame + audio decoding; that path is owned by VideoPlayer.tsx.
//
// Cache strategy:
//   • Decoded `AudioBuffer`s live in a module-level Map keyed by an opaque
//     `cacheKey` string the caller provides (typically the take id or stream
//     URL). Bounded by `MAX_CACHE_ENTRIES`. A simple insertion-order LRU
//     evicts the oldest entry when over capacity — buffers are typically
//     <1MB each, so eviction protects against truly long sessions, not
//     against bloat from a handful of takes.
//   • The cache is dropped on `invalidatePreviewAudio()` (e.g. after video
//     export or large-blob load on Safari) by subscribing to the same
//     `onPreviewAudioInvalidate` channel `useTimelinePreview` uses. Buffers
//     bound to a closed AudioContext are unusable; clearing forces re-decode
//     on the next play().

import {
  ensureVoiceGain,
  getAudioContext,
  isSafariMediaStreamPath,
  onPreviewAudioInvalidate,
  startSafariAudioTap,
} from '@/lib/audioContext';
import { effectiveVoiceVolume, useMixerStore } from '@/stores/mixer';

// ── Public API ────────────────────────────────────────────────────────────

export interface PlayHandle {
  /** Stop playback immediately. Idempotent — safe to call after onEnded. */
  stop(): void;
  /** Pause at the current position. Resume()-able. */
  pause(): void;
  /** Resume from the paused position. No-op if already playing or stopped. */
  resume(): void;
  /** Seek to `timeSec` from the start of the buffer. Restarts the underlying
   *  source — Web Audio sources are one-shot, so seek is implemented as
   *  stop + new source at the requested offset. */
  seek(timeSec: number): void;
  /** True between `start()` and `onEnded` (or `stop()`). */
  isPlaying(): boolean;
  /** Current playback position in seconds (0..duration). Pause-aware. */
  getCurrentTime(): number;
  /** Decoded buffer duration in seconds. */
  getDuration(): number;
}

/** Routing target for the playback gain.
 *  • 'direct': straight to ac.destination — used for VersionsBar/version
 *    pills which historically lived OUTSIDE the mixer (parity with vanilla
 *    `new Audio(url)`).
 *  • 'voice-bus': through the shared `voiceGain` from `audioContext.ts` so
 *    mixer fader + mute + Safari MediaStream tap apply consistently. Used
 *    for TTS take chips so a chip plays at the same level as a Timeline
 *    preview of that same take. */
export type Routing = 'direct' | 'voice-bus';

export interface PlayOptions {
  /** AudioBuffer to play directly, or ArrayBuffer that will be decoded. */
  source: AudioBuffer | ArrayBuffer;
  /** Reuse a cached decoded AudioBuffer keyed on this string. When the
   *  caller passes an ArrayBuffer with a `cacheKey`, the decode result is
   *  also stored. Omit for one-off buffers (no caching). */
  cacheKey?: string;
  /** Linear gain 0..1 applied at create-time via a per-play GainNode.
   *  Default 1. */
  gain?: number;
  /** Routing — see `Routing`. Default 'direct'. */
  routing?: Routing;
  /** Where in the buffer to start (seconds). Default 0. */
  startFromSec?: number;
  /** rAF-driven (~60fps) progress updates. */
  onTimeUpdate?(t: number): void;
  /** Fires when playback reaches the natural end (NOT when stop() is
   *  called) — `stop()` is the user's intent, no callback needed. */
  onEnded?(): void;
  /** Fires when stop() is called externally (e.g. via stopAllPlayers).
   *  Mirror of onEnded for consumers that need to clean up state regardless
   *  of how playback ended. Does NOT fire on natural end (use onEnded). */
  onStop?(): void;
  /** Fires when the AudioBufferSourceNode actually started — useful for
   *  flipping a "loading→playing" UI state. The handle has already been
   *  resolved by then; this is a same-tick hint. */
  onStarted?(): void;
  /** Voice-bus routing only — when true, compensate the per-play gain so
   *  playback comes out at unity reference level regardless of where the
   *  mixer's Voice fader sits. Used by inspection-style chip/take previews
   *  that should always be at full volume so the talent can audit the take
   *  cleanly even with the fader pulled down. Live-tracks fader changes
   *  during playback (re-applies the inverse on every update). A muted
   *  fader (effective=0) plays at unity instead of silence so the
   *  inspection use-case isn't broken by a forgotten mute. */
  bypassMixer?: boolean;
}

/** Start playback. Resolves once the source is scheduled — typically <1ms
 *  on Chrome, slightly more on Safari (context resume). The returned
 *  handle is the only legitimate way to control or query playback. */
export async function playAudio(opts: PlayOptions): Promise<PlayHandle> {
  const ac = getAudioContext();
  // Resume if needed — the user gesture that triggered this call has
  // already authorised resumption. Safari is happy to keep going if the
  // context is already running.
  if (ac.state === 'suspended') {
    try { await ac.resume(); } catch { /* ignore */ }
  }

  const buffer = await resolveBuffer(opts.source, opts.cacheKey, ac);

  return new Player(ac, buffer, opts).start();
}

/** Decode an ArrayBuffer once and store the resulting AudioBuffer under
 *  `key`. Subsequent `playAudio` calls with the same key skip decode. */
export async function decodeAndCache(
  buffer: ArrayBuffer,
  key: string,
): Promise<AudioBuffer> {
  return resolveBuffer(buffer, key, getAudioContext());
}

/** Fetch + decode + play a remote audio URL. Caches the decoded buffer by
 *  `url` so a second play of the same version pill is instant. The fetch
 *  uses the default browser cache; the server's `/stream/...` endpoint
 *  serves with appropriate cache headers. */
export async function playFromUrl(
  url: string,
  opts: Omit<PlayOptions, 'source' | 'cacheKey'> & {
    /** Override the default url-as-cache-key. Useful when the same URL
     *  carries different content per session (it doesn't here, but the
     *  hook layer might want to namespace). */
    cacheKey?: string;
    /** Fires when the network fetch is in flight. Allows callers to show a
     *  spinner while large composite WAVs (~5-10MB) buffer. The cache hit
     *  path skips this entirely. */
    onLoading?(): void;
  },
): Promise<PlayHandle> {
  const key = opts.cacheKey ?? url;
  const ac = getAudioContext();

  // Cache hit — straight to play. This is the FB#2 win: VersionsBar replays
  // are instant instead of re-buffering 5MB. AudioBuffer doesn't expose its
  // owning context as a property, so context-staleness is handled via the
  // `onPreviewAudioInvalidate` subscription that clears the cache on close.
  const cached = bufferCache.get(key);
  if (cached) {
    bumpLru(key);
    return new Player(ac, cached, { ...opts, source: cached, cacheKey: key }).start();
  }

  opts.onLoading?.();

  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) throw new Error(`audio fetch failed: ${resp.status}`);
  const ab = await resp.arrayBuffer();
  // Resume after the fetch (the user gesture is still valid in modern
  // browsers across awaits inside the same handler).
  if (ac.state === 'suspended') {
    try { await ac.resume(); } catch { /* ignore */ }
  }
  const decoded = await resolveBuffer(ab, key, ac);
  return new Player(ac, decoded, { ...opts, source: decoded, cacheKey: key }).start();
}

/** Stop every active player. Used when navigating away from a screen that
 *  spawned playback (Generation unmount, asset switch). */
export function stopAllPlayers(): void {
  for (const p of activePlayers) {
    try { p.stop(); } catch { /* ignore */ }
  }
  activePlayers.clear();
}

// ── Cache + LRU ──────────────────────────────────────────────────────────

const MAX_CACHE_ENTRIES = 50;
// Insertion-order Map = LRU — re-set on access to bump to most-recent.
const bufferCache: Map<string, AudioBuffer> = new Map();

function bumpLru(key: string): void {
  const v = bufferCache.get(key);
  if (!v) return;
  bufferCache.delete(key);
  bufferCache.set(key, v);
}

function evictIfOver(): void {
  while (bufferCache.size > MAX_CACHE_ENTRIES) {
    const oldest = bufferCache.keys().next().value;
    if (oldest === undefined) break;
    bufferCache.delete(oldest);
  }
}

/** Resolve `source` to a decoded AudioBuffer, with optional cache lookup +
 *  store. AudioBuffer source returns directly. ArrayBuffer source decodes
 *  (consuming the buffer — see the `.slice(0)` copy below). */
async function resolveBuffer(
  source: AudioBuffer | ArrayBuffer,
  cacheKey: string | undefined,
  ac: AudioContext,
): Promise<AudioBuffer> {
  if (isAudioBuffer(source)) {
    if (cacheKey && !bufferCache.has(cacheKey)) {
      bufferCache.set(cacheKey, source);
      evictIfOver();
    } else if (cacheKey) {
      bumpLru(cacheKey);
    }
    return source;
  }

  if (cacheKey) {
    const cached = bufferCache.get(cacheKey);
    // The cache is dropped on `onPreviewAudioInvalidate` so any survivor
    // here is bound to a still-valid context. Re-decoding on suspicion
    // would be safer but more expensive; defer to the invalidate channel.
    if (cached && cached.sampleRate > 0) {
      bumpLru(cacheKey);
      return cached;
    }
    if (cached) bufferCache.delete(cacheKey);
  }

  // decodeAudioData consumes its input ArrayBuffer (transfer semantics on
  // some impls). Always pass a copy so the caller's reference stays usable
  // for IndexedDB persistence + re-decode after invalidate.
  const copy = source.slice(0);
  const decoded = await ac.decodeAudioData(copy);
  if (cacheKey) {
    bufferCache.set(cacheKey, decoded);
    evictIfOver();
  }
  return decoded;
}

function isAudioBuffer(x: unknown): x is AudioBuffer {
  return (
    typeof x === 'object' &&
    x !== null &&
    typeof (x as AudioBuffer).getChannelData === 'function' &&
    typeof (x as AudioBuffer).numberOfChannels === 'number'
  );
}

// Drop the cache when the preview AudioContext is invalidated. Buffers
// bound to a closed context are unusable; the next play() will re-decode.
onPreviewAudioInvalidate(() => {
  bufferCache.clear();
});

// ── Bypass-mute refcount ───────────────────────────────────────────────────
// bypassMixer means "play at unity even if the user has the fader pulled
// down". A non-zero fader is handled by per-play gain compensation
// (gain * 1/mix). But MUTE sets voiceGain.gain to 0, which can't be undone
// downstream — the bus itself silences everything. So we temporarily
// un-mute via the store and restore on the LAST bypass play that finishes.
// A refcount lets concurrent bypass plays share the snapshot without one
// finishing player restoring the mute mid-play of another.
let bypassMuteRefcount = 0;
let bypassMuteSnapshot: boolean | null = null;

// ── Player implementation ────────────────────────────────────────────────

const activePlayers = new Set<Player>();

class Player implements PlayHandle {
  private ac: AudioContext;
  private buffer: AudioBuffer;
  private opts: PlayOptions;
  private gainNode: GainNode | null = null;
  private source: AudioBufferSourceNode | null = null;
  private playing = false;
  /** Offset within the buffer that the CURRENT source was started from
   *  (seconds). Used to compute getCurrentTime() and to recreate the
   *  source after pause/seek. */
  private bufferStartOffset = 0;
  /** ac.currentTime at which the current source was scheduled to start.
   *  Used for getCurrentTime() = bufferStartOffset + (now - contextStart). */
  private contextStartTime = 0;
  /** Where to resume after pause(). */
  private pausedAt: number | null = null;
  private rafHandle: number | null = null;
  /** Latched on natural-end so the `ended` event from a stop()-induced stop
   *  doesn't double-fire onEnded. */
  private endedFired = false;
  /** Live mixer-subscription handle for bypassMixer mode — unsubscribed in
   *  teardownSource so a finished play doesn't keep updating its dead gain. */
  private bypassUnsub: (() => void) | null = null;
  /** Whether THIS player incremented the bypass-mute refcount at start. Used
   *  to ensure the matching decrement-on-teardown only runs once per player. */
  private heldBypassMute = false;

  constructor(ac: AudioContext, buffer: AudioBuffer, opts: PlayOptions) {
    this.ac = ac;
    this.buffer = buffer;
    this.opts = opts;
  }

  start(): this {
    activePlayers.add(this);
    this.scheduleSource(this.opts.startFromSec ?? 0);
    this.opts.onStarted?.();
    if (this.opts.onTimeUpdate) this.startRaf();
    return this;
  }

  // ── PlayHandle ──────────────────────────────────────────────────────

  stop(): void {
    this.teardown(false);
  }

  pause(): void {
    if (!this.playing) return;
    this.pausedAt = this.getCurrentTime();
    // pause is mid-buffer — needs the click-suppression ramp like stop().
    this.teardownSource(false);
    this.playing = false;
    this.stopRaf();
  }

  resume(): void {
    if (this.playing || this.pausedAt == null) return;
    this.scheduleSource(this.pausedAt);
    this.pausedAt = null;
    if (this.opts.onTimeUpdate) this.startRaf();
  }

  seek(timeSec: number): void {
    const clamped = Math.max(0, Math.min(this.buffer.duration, timeSec));
    if (this.playing) {
      // seek is also mid-buffer — keep the ramp.
      this.teardownSource(false);
      this.scheduleSource(clamped);
    } else {
      this.pausedAt = clamped;
    }
  }

  isPlaying(): boolean {
    return this.playing;
  }

  getCurrentTime(): number {
    if (this.pausedAt != null) return this.pausedAt;
    if (!this.playing) return this.bufferStartOffset;
    return Math.min(
      this.buffer.duration,
      this.bufferStartOffset + (this.ac.currentTime - this.contextStartTime),
    );
  }

  getDuration(): number {
    return this.buffer.duration;
  }

  // ── Internals ──────────────────────────────────────────────────────

  private scheduleSource(fromSec: number): void {
    const ac = this.ac;
    const src = ac.createBufferSource();
    src.buffer = this.buffer;

    // Per-play gain. Routing decides where this gain feeds:
    //   • 'voice-bus' → through the shared voiceGain node (Safari MediaStream
    //     tap + live mixer fader subscription). The per-play gain is JUST
    //     userGain — voiceGain.gain.value already carries the effective mixer
    //     voice volume (live-subscribed in audioContext.ts:ensureVoiceGain),
    //     so multiplying by mix here would double-apply it (chips would play
    //     at mix² instead of mix — visibly quieter than timeline output that
    //     only goes through voiceGain).
    //   • 'voice-bus' + bypassMixer → cancel out voiceGain's mix so playback
    //     comes out at unity. Live re-applied on fader changes.
    //   • 'direct' → straight to destination, no mixer involvement.
    const gainNode = ac.createGain();
    const userGain = this.opts.gain ?? 1;
    if (this.opts.routing === 'voice-bus') {
      // bypassMixer + currently muted → temporarily un-mute so the bus can
      // pass audio. Refcount-tracked so concurrent bypass plays share the
      // snapshot without one finishing player restoring mute mid-play of
      // another. Restoration runs on teardown when refcount returns to 0.
      if (this.opts.bypassMixer) {
        const s = useMixerStore.getState();
        if (s.voiceMuted) {
          if (bypassMuteRefcount === 0) {
            bypassMuteSnapshot = true;
            s.setVoiceMuted(false);
          }
          bypassMuteRefcount++;
          this.heldBypassMute = true;
        }
      }

      // Re-read mix AFTER the potential un-mute above.
      const mix = effectiveVoiceVolume(useMixerStore.getState());
      // ensureVoiceGain rebinds Safari's MediaStream tap when the context
      // is fresh.
      const voiceGain = ensureVoiceGain(mix);
      if (this.opts.bypassMixer) {
        // Inverse of voiceGain.gain so net = userGain. Live-tracked: store
        // subscription updates this on fader changes during playback.
        const compensate = (m: number) => {
          const denom = m > 0.001 ? m : 1;
          gainNode.gain.value = userGain / denom;
        };
        compensate(mix);
        this.bypassUnsub = useMixerStore.subscribe((s) => {
          compensate(effectiveVoiceVolume(s));
        });
      } else {
        gainNode.gain.value = userGain;
      }
      if (voiceGain.context === ac) {
        gainNode.connect(voiceGain);
      } else {
        gainNode.connect(ac.destination);
      }
    } else {
      gainNode.gain.value = userGain;
      gainNode.connect(ac.destination);
    }

    // Safari MediaStream path: re-attach srcObject on every schedule.
    // Mirrors the timeline-preview's `startSafariAudioTap()` rule. The
    // gain node above already routed through voiceGain (the MediaStream
    // tap branch) when in voice-bus mode, so this just kicks the bridging
    // <audio> element back on.
    if (this.opts.routing === 'voice-bus' && isSafariMediaStreamPath()) {
      startSafariAudioTap();
    }

    src.connect(gainNode);
    src.onended = () => this.onSourceEnded(src);

    this.source = src;
    this.gainNode = gainNode;
    this.bufferStartOffset = fromSec;
    this.contextStartTime = ac.currentTime;
    this.playing = true;
    this.endedFired = false;

    src.start(0, fromSec);
  }

  /** `ended` fires for both natural completion and our own stop() (via
   *  src.stop()). Distinguish via the `playing` flag — stop() flips it
   *  before calling src.stop(), so a true `playing` here means natural
   *  end-of-buffer. */
  private onSourceEnded(src: AudioBufferSourceNode): void {
    if (src !== this.source) return; // stale callback after seek/pause
    if (!this.playing) return;       // stop()/pause() already handled
    if (this.endedFired) return;
    this.endedFired = true;
    this.playing = false;
    this.stopRaf();
    activePlayers.delete(this);
    // Tear down the source + bypass-mute refcount on natural end too. Without
    // this, heldBypassMute leaks on every play that finishes by reaching the
    // buffer's end (vs. user-initiated stop), which means the mute snapshot
    // is never restored when the LAST player ends naturally.
    //
    // CRITICAL: pass natural=true so teardownSource SKIPS the click-
    // suppression ramp + 16ms deferred disconnect. The source has already
    // played its final sample silently — there's no discontinuity to mask.
    // Holding the audio graph alive for an extra 16ms here was causing the
    // Safari MediaStream tap (voice-bus routing) to flush trailing samples
    // through the bridging <audio> element AFTER the buffer was nominally
    // done, audible as a stutter at natural end of every take preview.
    this.teardownSource(true);
    this.opts.onEnded?.();
  }

  /** Tear down the source + gain node. `natural=true` means the source has
   *  already finished playing on its own (onended path) — disconnect
   *  immediately, no ramp, no deferred stop. `natural=false` means the
   *  caller is interrupting mid-buffer (stop / pause / seek) and the ramp
   *  is needed to suppress the click + Safari MediaStream-tap stutter. */
  private teardownSource(natural: boolean): void {
    const src = this.source;
    const gainNode = this.gainNode;

    // Sever the bypass subscription NOW so it doesn't fight the ramp by
    // re-writing gainNode.gain.value during the ramp window (interrupt path)
    // or hold a dead reference (natural path).
    if (this.bypassUnsub) {
      try { this.bypassUnsub(); } catch { /* ignore */ }
      this.bypassUnsub = null;
    }

    if (natural) {
      // Source already produced its final sample and onended fired — the
      // graph is silent. Drop everything synchronously so Safari's
      // MediaStream tap / bridging <audio> element doesn't have a 16ms
      // window to flush trailing samples (which surfaced as the
      // "stutter at natural end" regression). No ramp, no setTimeout.
      if (src) {
        try { src.onended = null; } catch { /* ignore */ }
        try { src.disconnect(); } catch { /* ignore */ }
      }
      if (gainNode) {
        try { gainNode.disconnect(); } catch { /* ignore */ }
      }
    } else {
      // Hard-stopping a BufferSource mid-buffer creates a discontinuity at
      // the last rendered sample — audible as a click on the direct path
      // and as a *stutter* on Safari's MediaStream tap, where the bridging
      // <audio> element keeps flushing buffered samples for ~10-30ms after
      // src.stop(). (The latter is the same family of bug
      // `stopAllPreviewSources()` mitigates by nulling audioEl.srcObject;
      // we can't do that here without killing concurrent voice-bus playback
      // from sibling lanes.)
      //
      // Two-phase stop: ramp gain to 0 FAST (5ms — no click but near-
      // immediate silence), then KEEP THE SOURCE RUNNING with that 0
      // gain for HOLD_SEC so Safari's MediaStream-tap audio element
      // drains its ~30-60ms internal buffer with silent samples. Without
      // the hold, the audio element keeps flushing pre-ramp samples
      // (full gain — even louder when bypassMixer's voice-fader
      // compensation pushed gain >1) for tens of milliseconds after
      // src.stop() landed → audible stutter / tail.
      //
      // 5ms is well under the click-perception threshold; 80ms is well
      // over Safari's tap buffer; total perceived stop-latency is the
      // 80ms hold which is still subliminal as "intent → silence".
      const RAMP_SEC = 0.005;
      const HOLD_SEC = 0.080;
      if (gainNode) {
        try {
          const ac = this.ac;
          const t0 = ac.currentTime;
          // cancelScheduledValues + setValueAtTime to anchor the ramp at
          // the current gain — without this, an in-flight bypass-comp
          // setter or mixer-fader subscription update could overwrite our
          // ramp midflight.
          gainNode.gain.cancelScheduledValues(t0);
          gainNode.gain.setValueAtTime(gainNode.gain.value, t0);
          gainNode.gain.linearRampToValueAtTime(0, t0 + RAMP_SEC);
        } catch { /* ignore — fall through to hard stop */ }
      }
      if (src) {
        // Drop onended immediately so the deferred stop doesn't trip the
        // natural-end path. Schedule the actual src.stop() AFTER the
        // hold window so silent samples get pumped through the tap buffer
        // before the source goes away.
        try { src.onended = null; } catch { /* ignore */ }
        const ac = this.ac;
        const stopAt = ac.currentTime + HOLD_SEC;
        try { src.stop(stopAt); } catch {
          try { src.stop(); } catch { /* ignore */ }
        }
        // Disconnect AFTER the source has stopped + a small extra margin
        // for the audio element's last drain frame.
        const srcRef = src;
        const gainRef = gainNode;
        window.setTimeout(() => {
          try { srcRef.disconnect(); } catch { /* ignore */ }
          if (gainRef) {
            try { gainRef.disconnect(); } catch { /* ignore */ }
          }
        }, Math.ceil(HOLD_SEC * 1000) + 16);
      } else if (gainNode) {
        // No source but a gain node lingered — disconnect it directly.
        // Can happen if scheduleSource threw between createGain and start.
        try { gainNode.disconnect(); } catch { /* ignore */ }
      }
    }

    if (this.heldBypassMute) {
      this.heldBypassMute = false;
      bypassMuteRefcount = Math.max(0, bypassMuteRefcount - 1);
      if (bypassMuteRefcount === 0 && bypassMuteSnapshot !== null) {
        try {
          useMixerStore.getState().setVoiceMuted(bypassMuteSnapshot);
        } catch { /* ignore */ }
        bypassMuteSnapshot = null;
      }
    }
    this.source = null;
    this.gainNode = null;
  }

  private teardown(natural: boolean): void {
    const wasPlaying = this.playing;
    this.playing = false;
    // teardown(true) is only reached via the public stop() path being told
    // "this was a natural stop" — there's no caller using that today, but
    // keep the natural flag plumbed through so future callers don't have to
    // re-derive the distinction. The interrupt ramp is what stop() needs.
    this.teardownSource(natural);
    this.stopRaf();
    activePlayers.delete(this);
    if (natural && !this.endedFired) {
      this.endedFired = true;
      this.opts.onEnded?.();
    } else if (!natural && wasPlaying) {
      this.opts.onStop?.();
    }
  }

  private startRaf(): void {
    if (this.rafHandle != null) return;
    const tick = () => {
      if (!this.playing) {
        this.rafHandle = null;
        return;
      }
      this.opts.onTimeUpdate?.(this.getCurrentTime());
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private stopRaf(): void {
    if (this.rafHandle != null) {
      try { cancelAnimationFrame(this.rafHandle); } catch { /* ignore */ }
      this.rafHandle = null;
    }
  }
}
